import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  TextField,
  Typography
} from "@mui/material";
import { createGroup, getGroupsByCompanyId, updateGroup, deleteGroup } from "../axios/axiosFunctions";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TeamsCards360 from "./TeamsCards360";
import DeleteConfirmationModal from "./common/DeleteConfirmationModal";

const TeamSection360 = ({ companyId, category }) => {
  const [allGroups, setAllGroups] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newGroup, setNewGroup] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [alertMsg, setAlertMsg] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Edit team state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editTeam, setEditTeam] = useState({ id: "", name: "" });

  // Delete team state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState({ id: "", name: "" });

  useEffect(() => {
    callGroups();
  }, [companyId]);

  const callGroups = async () => {
    const groups = await getGroupsByCompanyId(companyId, category);
    setAllGroups(groups);
    setLoading(false);
  }

  const handleDialogClose = () => {
    setOpenDialog(false);
    setNewGroup("");
  }

  const handleChange = (e) => {
    const { value } = e.target;
    setNewGroup(value);
  }

  const handleCreateTeam = async () => {
    if (newGroup === "") {
      setAlertMsg("Please fill the name");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
      return
    }
    const data = {
      name: newGroup,
      company: companyId,
      category,
    };

    const res = await createGroup(data);
    if (!res) {
      setAlertMsg("Error while creating the team, please try later o contact an admin");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
    }

    setAlertMsg("Team Created");
    setAlertSeverity("success");
    setAlertOpen(true);
    setTimeout(() => {
      callGroups();
      setAlertOpen(false);
      handleDialogClose();
    }, 2000);
  }

  // Edit team handlers
  const handleOpenEditDialog = (team) => {
    setEditTeam({ id: team._id, name: team.name });
    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
    setEditTeam({ id: "", name: "" });
  };

  const handleEditTeamChange = (e) => {
    setEditTeam({ ...editTeam, name: e.target.value });
  };

  const handleUpdateTeam = async () => {
    if (editTeam.name === "") {
      setAlertMsg("Please fill the name");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
      return;
    }

    const res = await updateGroup(editTeam.id, { name: editTeam.name });
    if (!res) {
      setAlertMsg("Error while updating the team, please try later or contact admin");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
      return;
    }

    setAlertMsg("Team Updated");
    setAlertSeverity("success");
    setAlertOpen(true);
    setTimeout(() => {
      callGroups();
      setAlertOpen(false);
      handleEditDialogClose();
    }, 2000);
  };

  // Delete team handlers
  const handleOpenDeleteDialog = (team) => {
    setTeamToDelete({ id: team._id, name: team.name });
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setTeamToDelete({ id: "", name: "" });
  };

  const handleDeleteTeam = async () => {
    const res = await deleteGroup(teamToDelete.id);
    if (!res) {
      setAlertMsg("Error while deleting the team, please try later or contact admin");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
      handleDeleteDialogClose();
      return;
    }

    setAlertMsg("Team Deleted");
    setAlertSeverity("success");
    setAlertOpen(true);
    setTimeout(() => {
      callGroups();
      setAlertOpen(false);
      handleDeleteDialogClose();
    }, 2000);
  };

  return (
    <>
      <Typography variant="h6" color="secondary">Teams</Typography>
      <Divider />
      <Button onClick={() => setOpenDialog(true)} variant="contained" sx={{ color: "white", marginTop: "20px", marginBottom: "20px" }} endIcon={<AddCircleOutlineIcon />}>ADD TEAMS</Button>
      {alertOpen && (
        <Alert sx={{ marginBottom: "20px" }} severity={alertSeverity}>
          {alertMsg}
        </Alert>
      )}
      {
        !loading &&
        <Grid container spacing={2}>
          {
            allGroups.length === 0 ?
              <Typography variant="h6">No teams</Typography>
              :
              <>
                {
                  allGroups.map((g, i) => (
                    <TeamsCards360
                      key={i}
                      group={g}
                      callGroups={callGroups}
                      onEdit={() => handleOpenEditDialog(g)}
                      onDelete={() => handleOpenDeleteDialog(g)}
                    />
                  ))
                }
              </>
          }

          {/* Create Team Dialog */}
          <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
            <DialogContent>
              <Typography variant='h6' sx={{ textAlign: "center" }}>Create Team</Typography>
              <Typography variant="subtitle1">Team Name</Typography>
              <TextField onChange={handleChange} fullWidth />
              {
                alertOpen &&
                <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
                  {alertMsg}
                </Alert>
              }
              <Box sx={{ display: "flex", margin: "20px", justifyContent: "center" }}>
                <Button onClick={handleCreateTeam} variant='contained' sx={{ marginRight: "20px", justifyContent: "space-around" }}>CREATE</Button>
                <Button variant='contained' onClick={handleDialogClose} color="customGray">CANCEL</Button>
              </Box>
            </DialogContent>
          </Dialog>

          {/* Edit Team Dialog */}
          <Dialog open={openEditDialog} onClose={handleEditDialogClose} maxWidth="md" fullWidth>
            <DialogContent>
              <Typography variant='h6' sx={{ textAlign: "center" }}>Edit Team</Typography>
              <Typography variant="subtitle1">Team Name</Typography>
              <TextField
                value={editTeam.name}
                onChange={handleEditTeamChange}
                fullWidth
              />
              {
                alertOpen &&
                <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
                  {alertMsg}
                </Alert>
              }
              <Box sx={{ display: "flex", margin: "20px", justifyContent: "center" }}>
                <Button onClick={handleUpdateTeam} variant='contained' sx={{ marginRight: "20px" }}>UPDATE</Button>
                <Button variant='contained' onClick={handleEditDialogClose} color="customGray">CANCEL</Button>
              </Box>
            </DialogContent>
          </Dialog>

          {/* Delete Team Confirmation Dialog */}
          <DeleteConfirmationModal
            openDialog={openDeleteDialog}
            closeDialog={handleDeleteDialogClose}
            title={`Delete Team: ${teamToDelete.name}`}
            message="All team members and their data will be removed from this team."
            onConfirm={handleDeleteTeam}
          />
        </Grid>
      }
    </>
  )
}

export default TeamSection360
